/**
 * The editorial IA — hubs (code), and the SHAPE of the reviews tree (scopes → types).
 *
 * The tree's CONTENT is WordPress's since v6 (`GET /wp-json/api/editorial-tree`, read by
 * `lib/editorial/tree.ts`): editors add a type, reorder types, rename a label in wp-admin and
 * the site follows within a cache cycle, no deploy. This file keeps the compiled-in
 * FALLBACK tree — what the site serves when WordPress has not answered yet (cold process,
 * endpoint not deployed on that install, outage) — plus the hubs, which are code: a hub is a
 * route file, a list layout and a set of title/intro templates. What a hub SAYS on its root
 * page (`/best/`, `/guides/`) is WordPress's too: `hubs[]` of the same endpoint carries the
 * hub category's name, SEO title and description (`EditorialTree.hubRoots`).
 *
 * The header's Coverage column and the coverage pages it links to must agree on what
 * "Earbuds" means, or the count and the page disagree again (the defect this whole layer
 * exists to prevent). So both read the SAME loaded tree (`loadEditorialTree`), never this
 * fallback directly.
 *
 * Keyed by slug, never by WordPress term id — ids are per-install, and a wrong one returns a
 * plausible count for the wrong term. The WP tree carries ids; the fallback resolves them.
 *
 * Client components import only the hubs, the types and the href helpers from here. The
 * resolved scope/type travel as PROPS (`lib/editorial/page.tsx`): re-deriving the tree in the
 * browser from this fallback would server-render an editor-created type and blank it on
 * hydration.
 *
 * Glossary — "guide" is overloaded across this codebase, so:
 *  - **guide**: one WP best-list article (`/best/<slug>` → `pages/best/[slug].tsx`,
 *    analytics `page_type: 'guide_page'`, `lib/editorial/guidePicks.ts`).
 *  - **best hub**: the directory OF guides — hub id `'best'`, variant `'best'`,
 *    `/best/<scope>`.
 *  - **guides hub**: how-to articles, NOT best-lists — hub id `'guides'`, variant
 *    `'articles'`, `/guides/<scope>`, root `/guides/`; its WordPress category is `how-to`.
 */

/**
 * A product TYPE inside a scope — one WP child topic (`wireless-earbuds`), the level below
 * the scope. WP rule (`Topic::editorialPlaceById`): a type is the highest scoped topic of its
 * branch THAT HAS A PARENT; a scoped top-level topic is the scope's own member and never a
 * type (`/reviews/speakers/speakers/` does not exist). Topics below a type belong to it.
 */
export interface EditorialType {
  /** WP `topic` slug — doubles as the path segment: `/reviews/earbuds/wireless-earbuds`. */
  slug: string
  /** Noun for titles and prose: "wireless earbuds reviews". Lowercase. */
  noun: string
  /** Short pill label beside its siblings, where the scope is already stated: "True wireless". */
  label: string
  /** WP `editorial_intro` — replaces the hub's whole intro sentence on the type page. */
  intro: string | null
  /**
   * WP term ids the type's list queries: the type and its descendants. `[]` only on a
   * fallback tree whose slug could not be resolved because WP was unreachable
   * (`EditorialTree.degraded`) — a transient state, not a missing term.
   */
  topicIds: number[]
}

export interface EditorialScope {
  id: string
  label: string
  /** Lowercase noun for prose: "415 earbud reviews". */
  short: string
  /**
   * What follows "Every" in the reviews intro when "pair of <short>" reads wrong: "Every
   * music streaming service we've measured". Null = `pair of ${short}`.
   */
  subject: string | null
  /**
   * The registry row's `intro` (WP "Reviews tree" page) — replaces the reviews hub's whole
   * intro sentence on the scope page, as a type's `editorial_intro` does on its type page.
   */
  intro: string | null
  /**
   * The scope's OWN top-level WP topic slugs — `speakers` → `['speakers']`; `earbuds` → `[]`
   * because WordPress has no such term (the taxonomy carries one combined
   * `earbuds-headphones` parent with type children). Slug-keyed matching only: the archive
   * link and the WP hub chips. Never the query — that is `topicIds`.
   */
  topicSlugs: string[]
  /**
   * Every WP term id in the scope — its own topics, its types and their descendants. THE
   * query behind every count and list of the scope (`tax[topic][]=a&[]=b`, verified OR).
   */
  topicIds: number[]
  /** In display order. Empty for a scope with nothing to partition (speakers today). */
  types: EditorialType[]
  /**
   * DealHunt catalogue node — the product side of the same scope. Only scopes with one are
   * header tabs (a tab is half Browse, half Coverage); coverage pages never read it.
   */
  dealHuntSlug: string | null
  /**
   * The hubs this scope has pages on. Null = every hub (`/reviews/earbuds`, `/best/earbuds`,
   * `/versus/earbuds`…). A reviews-only scope lists `['reviews']`, so `/versus/microphones`
   * is a 404 and never a sitemap row or an empty "Nothing here yet".
   */
  hubs: readonly string[] | null
}

/** The resolved reviews tree every reader works from — see `lib/editorial/tree.ts`. */
export interface EditorialTree {
  /** In editor order. */
  scopes: EditorialScope[]
  /**
   * Hub id → the copy of its root page, from WordPress (`hubs[]`: the hub category's name,
   * Yoast title and description). Empty on the fallback tree and on a WordPress that does
   * not send `hubs` yet; read through `hubRootCopy`, which fills the gaps.
   */
  hubRoots: Readonly<Record<string, WpHubRoot>>
  /** `wp` = the WordPress registry; `fallback` = this file's compiled-in tree. */
  source: 'wp' | 'fallback'
  /**
   * True when part of the tree has no term ids because WordPress could not be asked
   * (fallback tree before or without resolution). A type without ids on a degraded tree is
   * "not yet known", never "does not exist".
   */
  degraded: boolean
}

/**
 * The compiled-in tree, in the compact form the code declares it. `topicSlugs` = the scope's
 * own top-level topics, `types` = its child-topic types; the scope's query is the union.
 * Mirrors what `wp sg editorial-seed` writes into WordPress (WP request v6, Ask 6).
 */
export interface FallbackScope {
  id: string
  label: string
  short: string
  subject?: string
  topicSlugs: string[]
  types?: readonly { slug: string; noun: string; label: string }[]
  dealHuntSlug?: string
  hubs?: readonly string[]
}

export const FALLBACK_SCOPES: readonly FallbackScope[] = [
  {
    id: 'earbuds',
    label: 'Earbuds',
    short: 'earbuds',
    topicSlugs: [],
    types: [
      { slug: 'wireless-earbuds', noun: 'wireless earbuds', label: 'True wireless' },
      { slug: 'wired-earbuds', noun: 'wired earbuds', label: 'Wired' },
    ],
    dealHuntSlug: 'electronics/audio/earbuds',
  },
  {
    id: 'headphones',
    label: 'Headphones',
    short: 'headphones',
    topicSlugs: [],
    // Gaming headsets is a PEER scope, not a headphones type (Guillaume, 2026-09-28: its
    // traffic and reader intent on SG earn it a first-class section — WP has always had
    // it as a top-level topic). This reverses the 2026-07-30 call that folded it in to
    // match DealHunt's catalogue, so the header's Browse column (catalogue: gaming under
    // Headphones, 154 products) and its Coverage column (reviews: 278 without gaming)
    // now describe slightly different worlds; the Coverage rows link `/reviews/headphones/`,
    // which is what they count.
    //
    // `open-ear-headphones` is a NEW WP topic (ticket v5 seed; on Pantheon test since
    // 2026-09-29, 4 reviews). On an install that has not been seeded the slug resolves to
    // no term and the type drops out of the fallback tree (its page 404s) — live is seeded
    // BEFORE this reaches production.
    types: [
      { slug: 'over-ear-headphones', noun: 'over-ear headphones', label: 'Over-ear' },
      { slug: 'on-ear-headphones', noun: 'on-ear headphones', label: 'On-ear' },
      { slug: 'open-ear-headphones', noun: 'open-ear headphones', label: 'Open-ear' },
    ],
    dealHuntSlug: 'electronics/audio/headphones',
  },
  {
    id: 'speakers',
    label: 'Speakers',
    short: 'speakers',
    subject: 'speaker',
    topicSlugs: ['speakers'],
    dealHuntSlug: 'electronics/audio/speakers',
  },
  {
    // Peer of the three families (see the headphones note). No DealHunt family node of
    // its own (the catalogue files it under headphones), so not a header tab. Its hubs
    // mirror what `wp sg editorial-seed` ticks (ticket 5 §2 rule 1: a hub where the
    // scope has ≥ 12 posts).
    id: 'gaming-headsets',
    label: 'Gaming headsets',
    short: 'gaming headsets',
    topicSlugs: ['gaming-headsets'],
    hubs: ['reviews', 'news', 'best', 'versus'],
  },
  // Editorial-only scopes (Guillaume, 2026-09-26): "/reviews/ integrates ALL the reviews".
  // These were the remaining chips on the WordPress reviews hub. No DealHunt node, so no
  // header tab. On another hub only where they have ≥ 12 posts (ticket 5 §2 rule 1): a
  // versus page for microphones (1 post) stays a 404.
  {
    id: 'microphones',
    label: 'Microphones',
    short: 'microphones',
    subject: 'microphone',
    topicSlugs: ['microphones'],
    hubs: ['reviews', 'news', 'best', 'guides'],
  },
  {
    id: 'music-streaming',
    label: 'Music streaming',
    short: 'music streaming',
    subject: 'music streaming service',
    topicSlugs: ['music-streaming'],
    hubs: ['reviews', 'news', 'opinions', 'guides'],
  },
  {
    // WP slug is `general-audio`; the site has always labelled it "Audio technology".
    id: 'audio-technology',
    label: 'Audio technology',
    short: 'audio technology',
    subject: 'piece of audio technology',
    topicSlugs: ['general-audio'],
    hubs: ['reviews', 'news', 'best', 'opinions', 'guides'],
  },
  // NOT a scope: `hearing-aids` (2 reviews, 17 posts — WP prod review 2026-09-26). A scope
  // page for it would be an indexed, sitemap-listed near-empty list; its two reviews are
  // on `/reviews/` itself. Re-add once it clears a real floor.
]

/**
 * The fallback tree as an `EditorialTree` with NO term ids — the shape a synchronous
 * reader can hold before WordPress has answered (`currentTree()`), and the reference the
 * loader validates a WordPress tree against. Ids arrive through `resolveFallbackTree()`.
 */
export const FALLBACK_TREE: EditorialTree = {
  scopes: FALLBACK_SCOPES.map(scope => ({
    id: scope.id,
    label: scope.label,
    short: scope.short,
    subject: scope.subject ?? null,
    intro: null,
    topicSlugs: [...scope.topicSlugs],
    topicIds: [],
    types: (scope.types ?? []).map(type => ({ ...type, intro: null, topicIds: [] })),
    dealHuntSlug: scope.dealHuntSlug ?? null,
    hubs: scope.hubs ?? null,
  })),
  hubRoots: {},
  source: 'fallback',
  degraded: true,
}

/** The scopes a hub serves — `/reviews/` has all of them, `/versus/` the three families. */
export function scopesForHub(tree: EditorialTree, hubId: string): EditorialScope[] {
  return tree.scopes.filter(s => scopeOnHub(s, hubId))
}

export function scopeOnHub(scope: EditorialScope, hubId: string): boolean {
  return !scope.hubs || scope.hubs.includes(hubId)
}

export function findScope(tree: EditorialTree, id: string | undefined): EditorialScope | null {
  return tree.scopes.find(s => s.id === id) ?? null
}

export function findType(
  scope: EditorialScope | null,
  slug: string | undefined
): EditorialType | null {
  return scope?.types.find(t => t.slug === slug) ?? null
}

/** Every WP topic slug the tree knows for a scope — its own topics and its types. */
export function scopeTopicSlugs(scope: EditorialScope): string[] {
  return [...scope.topicSlugs, ...scope.types.map(t => t.slug)]
}

/** Which list layout a hub's posts want — they are genuinely different objects. */
export type HubVariant = 'reviews' | 'best' | 'versus' | 'articles'

export interface EditorialHub {
  id: string
  /** Nav/tab label. */
  label: string
  /** For the phone menu's half-width coverage tiles, where `label` plus a three-digit count
   *  does not fit one line (~130px). Only needed when `label` is long. */
  shortLabel?: string
  /** WP `category` term slug. */
  categorySlug: string
  /** The hub's root page and the path prefix of its scope and type pages:
   *  `${path}/${scope}`. Not `categorySlug` on guides and opinions. */
  path: string
  variant: HubVariant
  /**
   * Types are PATHS on this hub — `/reviews/earbuds/wireless-earbuds` — served by
   * `pages/<hub>/[scope]/[type].tsx`, with their own canonical and sitemap rows. On every
   * other hub a type is still the `?filter=` pill that canonicalises to the scope page.
   * Turning this on for a hub means adding that route file IN THE SAME COMMIT: without it
   * nothing 404s — the `[...slug]` catch-all serves the flat WP topic page under the nested
   * URL. `tests/editorial-routes.spec.ts` fails a flag without its route.
   */
  typePaths?: true
  /**
   * Every scope page of this hub is subject to the floor: under `MIN_INDEXABLE_POSTS` it is
   * noindex and out of the sitemap (`lib/editorial/indexFloor.ts`). Type pages are floored
   * on every hub whatever this says (a type page starts at one post, the case the floor
   * exists for); on a hub without this flag, scope pages are floored unless listed in
   * `floorExempt`.
   */
  indexFloor?: true
  /**
   * Scope pages of a hub WITHOUT `indexFloor` that never noindex by count: the ones live
   * before the floor spread past reviews (4e56968e — the speakers best and how-to hubs, 21
   * and 20 posts, were indexed on prod and stay so). A scope added to the hub later is
   * floored like any new page. Ignored on a hub declaring `indexFloor`.
   */
  floorExempt?: readonly string[]
  /**
   * Compiled-in copy of the hub ROOT page, fallback ONLY — what `/reviews/` says while
   * WordPress has not answered. Every hub has a root page (`pages/<path>/index.tsx`,
   * see `hubRootHref`); its copy is WordPress's (`hubRootCopy`). Absent, the fallback is the
   * hub label and no intro.
   */
  rootFallback?: HubRoot
  /** Page title, given a scope and optionally a type: "Earbud reviews", "Wireless earbuds reviews". */
  title: (scope: EditorialScope, type?: EditorialType | null) => string
  /** Standfirst, given a scope and optionally a type. */
  intro: (scope: EditorialScope, type?: EditorialType | null) => string
}

export interface HubRoot {
  /** H1 — "Reviews". */
  title: string
  /** `<title>` when it should differ from the H1 — "Audio reviews" keeps the SERP title
   *  the WordPress page held. Without the " - SoundGuys" suffix. */
  metaTitle: string | null
  intro: string
}

/** A hub root's copy as WordPress sent it; null = WordPress holds none for that field. */
export interface WpHubRoot {
  title: string | null
  metaTitle: string | null
  intro: string | null
}

/**
 * The 12 scope pages of best, versus, guides and opinions that were indexed before the floor
 * reached those hubs (`floorExempt`): the three product families on each.
 */
const LIVE_BEFORE_THE_FLOOR: readonly string[] = ['earbuds', 'headphones', 'speakers']

export const EDITORIAL_HUBS: readonly EditorialHub[] = [
  {
    id: 'reviews',
    label: 'Reviews',
    categorySlug: 'reviews',
    path: '/reviews',
    variant: 'reviews',
    typePaths: true,
    indexFloor: true,
    rootFallback: {
      title: 'Reviews',
      // The WordPress page ranked as "Audio reviews - SoundGuys"; keep that SERP title.
      metaTitle: 'Audio reviews',
      intro:
        "Every product we've measured, scored out of 10 on the same rig — earbuds, headphones, speakers and more. Newest first.",
    },
    // Type pages lead with the noun, never with "best" — the flagship guides own
    // "best wireless earbuds" (same rule as the best hub's title, below).
    title: (s, t) => `${titleCase(t?.noun ?? s.short)} reviews`,
    // A type's WP `editorial_intro` replaces the whole sentence on its type page, the
    // scope's registry `intro` on the scope page; a scope's `subject` only replaces "pair of
    // <noun>" ("Every music streaming service we've measured").
    intro: (s, t) =>
      (t ? t.intro : s.intro) ??
      `Every ${
        t ? `pair of ${t.noun}` : s.subject ?? `pair of ${s.short}`
      } we've measured, scored out of 10 on the same rig. Newest first.`,
  },
  {
    id: 'best',
    label: 'Best Products',
    categorySlug: 'best',
    path: '/best',
    variant: 'best',
    typePaths: true,
    floorExempt: LIVE_BEFORE_THE_FLOOR,
    // "Buying guides", NOT "The best …" (SEO audit round 4, 2026-08-31): the old
    // title exact-matched the flagship guides' own head terms ("best headphones"),
    // so the hub competed with the money pages it exists to feed. The buying-guides
    // phrasing targets the SERP currently held by the stale 2024 explainer article
    // (headphones-buying-guide-8290) — the directory hub is the intended winner
    // there — while "best" stays in the intro and in every linked guide title. A type
    // page follows the same rule (2b673046): "Wireless earbuds buying guides", never
    // "Best wireless earbuds", which is guide 14313's query.
    title: (s, t) => `${titleCase(t?.noun ?? s.short)} buying guides`,
    // A type page drops "per type": it IS one.
    intro: (s, t) =>
      `Our buying guides for ${t?.noun ?? s.short}: a shortlist per use case, per budget${
        t ? '' : ', per type'
      }. Every pick is a set we've had in the lab.`,
  },
  {
    id: 'versus',
    label: 'Versus',
    categorySlug: 'versus',
    path: '/versus',
    variant: 'versus',
    typePaths: true,
    floorExempt: LIVE_BEFORE_THE_FLOOR,
    title: (s, t) => `${titleCase(t?.noun ?? s.short)} head-to-head`,
    intro: (s, t) =>
      `Direct comparisons between the ${
        t?.noun ?? s.short
      } people actually shortlist together, decided on measurements rather than vibes.`,
  },
  {
    id: 'guides',
    label: "Guides & How-to's",
    categorySlug: 'how-to',
    path: '/guides',
    variant: 'articles',
    typePaths: true,
    floorExempt: LIVE_BEFORE_THE_FLOOR,
    title: (s, t) => `${titleCase(t?.noun ?? s.short)} guides & how-to's`,
    intro: (s, t) =>
      `Pairing, fixing, fitting and caring for ${t?.noun ?? s.short} — newest first.`,
  },
  {
    id: 'opinions',
    label: 'Features & Opinions',
    shortLabel: 'Features',
    categorySlug: 'features',
    path: '/opinions',
    variant: 'articles',
    typePaths: true,
    floorExempt: LIVE_BEFORE_THE_FLOOR,
    title: (s, t) => `${titleCase(t?.noun ?? s.short)} features & opinions`,
    intro: (s, t) =>
      `The argument behind the numbers: what our writers think the ${
        t?.noun ?? s.short
      } industry is getting right, and where it keeps lying to you.`,
  },
  {
    // Ticket 5 (per-hub trees), F1. Not a header Coverage row (`lib/header-nav/config.ts`). A scope with empty `hubs` in
    // WordPress (the three families) is on every hub, so news pages exist for them the day
    // this ships, whatever the WP registry's news tick says.
    id: 'news',
    label: 'News',
    categorySlug: 'news',
    path: '/news',
    variant: 'articles',
    typePaths: true,
    indexFloor: true,
    title: (s, t) => `${titleCase(t?.noun ?? s.short)} news`,
    // Not the type's WP `editorial_intro`: that is written as reviews copy.
    intro: (s, t) =>
      `The latest on ${
        t?.noun ?? s.short
      }: launches, updates and the stories behind them. Newest first.`,
  },
]

/** A pill is only worth showing if it leads somewhere with substance. */
export const PILL_MIN_POSTS = 5

/**
 * A floored scope or type page below this many posts is `noindex, follow` (still linked
 * and crawlable) and left out of `sitemap-editorial.xml`. ONE full listing page
 * (`COVERAGE_PAGE_SIZE`): a page that cannot fill its first screen is not advertised as a
 * ranking page; everything above is meant to be indexed (SEO memo 2026-09-30 — the earlier
 * 24 was a house guess with no precedent and would have noindexed two live hubs). Live
 * today: open-ear 4 sits under it, music streaming 13 just clears it. Which pages are
 * floored: `hasIndexFloor` (`lib/editorial/indexFloor.ts`); the hub root is always indexed.
 */
export const MIN_INDEXABLE_POSTS = 12
/** How many tag pills survive to the row, after ranking by count. */
export const PILL_MAX_TAGS = 5

/**
 * The scoped coverage page for a hub × scope — `/reviews/earbuds/`. The ONE place this
 * URL shape lives: the server builds document rows with it, and both panels rebuild it
 * client-side (structure-first — the destination is knowable before the document lands).
 */
export function coverageHref(hubPath: string, scopeId: string, trailingSlash: boolean): string {
  return `${hubPath}/${scopeId}${trailingSlash ? '/' : ''}`
}

/**
 * Where "the hub" lives: `hub.path`, the parent of its scope pages, served as a coverage
 * page (`pages/<path>/index.tsx`). On guides and opinions that is NOT the WordPress
 * category URL: `/guides/` and `/opinions/` are the roots, and `/how-to/` and `/features/`
 * 301 to them from WordPress (owner, 2026-10-02 — a crumb Features & Opinions › Headphones
 * spanning `/features/` and `/opinions/headphones/` read as two sections). Crumbs,
 * sibling-hub links, header tiles and the sitemap all go through here.
 */
export function hubRootHref(hub: EditorialHub, trailingSlash: boolean): string {
  return `${hub.path}${trailingSlash ? '/' : ''}`
}

/**
 * What a hub's root page says: WordPress's copy field by field (the category name, its
 * Yoast title and description, edited in wp-admin), then the compiled-in fallback, then
 * the hub label and no intro.
 */
export function hubRootCopy(tree: EditorialTree, hub: EditorialHub): HubRoot {
  const wp = tree.hubRoots[hub.id]
  return {
    title: wp?.title ?? hub.rootFallback?.title ?? hub.label,
    metaTitle: wp?.metaTitle ?? hub.rootFallback?.metaTitle ?? null,
    intro: wp?.intro ?? hub.rootFallback?.intro ?? '',
  }
}

/** The type page under a scope — `/reviews/earbuds/wireless-earbuds/`. Only for `typePaths` hubs. */
export function typeHref(
  hubPath: string,
  scopeId: string,
  typeSlug: string,
  trailingSlash: boolean
): string {
  return `${hubPath}/${scopeId}/${typeSlug}${trailingSlash ? '/' : ''}`
}

/**
 * Where a WP topic slug should send a reader on a given hub, or null when the topic is
 * outside every scope (accessories, dacs…) and the WP topic page stays the only
 * destination. Used to re-point the hub page's editor-curated chips — WordPress only
 * knows topic slugs, so the mapping to coverage URLs has to live here.
 *
 * A type topic on a `typePaths` hub → the type page; any scope topic otherwise → the
 * scope page (a single-topic scope IS its topic; on hubs without type paths the scope
 * page is the closest honest landing). On a `typePaths` hub other than reviews the type
 * page is linked only when `typeCount` says it has ≥ 1 post: it 404s at zero, and a
 * reviews-tree type can be empty on another hub (open-ear has no news). Without a count
 * (the synchronous WP page mapper) the chip goes to the scope page (14cd6d80: never link
 * a zero-post type). Reviews keeps linking its types directly: its tree IS the reviews
 * taxonomy, and that is the behaviour the `/reviews/` chips shipped with.
 */
export function coverageHrefForTopic(
  tree: EditorialTree,
  hub: EditorialHub,
  topicSlug: string,
  trailingSlash: boolean,
  typeCount?: (typeSlug: string) => number | null
): string | null {
  const scope = tree.scopes.find(s => scopeTopicSlugs(s).includes(topicSlug))
  if (!scope || !scopeOnHub(scope, hub.id)) {
    return null
  }
  if (hub.typePaths && findType(scope, topicSlug)) {
    if (hub.id === 'reviews' || (typeCount?.(topicSlug) ?? 0) >= 1) {
      return typeHref(hub.path, scope.id, topicSlug, trailingSlash)
    }
  }
  return coverageHref(hub.path, scope.id, trailingSlash)
}

export function findHubByCategorySlug(categorySlug: string): EditorialHub | null {
  return EDITORIAL_HUBS.find(h => h.categorySlug === categorySlug) ?? null
}

export function findHub(id: string): EditorialHub | null {
  return EDITORIAL_HUBS.find(h => h.id === id) ?? null
}

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}
